import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// La letra (IBM Plex Sans) va en theme/fonts.css, servida desde el propio repositorio.
import './theme/global.css';
import { App } from './App';
import { ApiError } from './api/client';
import { AuthProvider } from './auth/AuthContext';
import { DocumentViewerProvider } from './components/overlay/DocumentViewer';
import { UploadQueueProvider } from './pages/ledger/UploadQueue';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Los datos de la fase 1 son de solo lectura y no cambian bajo los pies del usuario:
      // no hace falta refrescar al volver a la pestaña.
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      /**
       * UN solo reintento, y rápido.
       *
       * Con dos reintentos y una API inalcanzable, cada intento tarda lo que tarde el sistema
       * en dar el fallo (hasta 5 s si el nombre del host no resuelve), así que el usuario se
       * quedaba mirando el esqueleto de carga ~15 segundos antes de que apareciera el estado de
       * error. Un reintento cubre el parpadeo de red; a partir de ahí es mejor decir la verdad
       * y ofrecer el botón «Reintentar» que seguir fingiendo que carga.
       */
      retry: (failureCount, error) => {
        // No merece la pena reintentar un 404 ni un error de validación; sí un fallo de red.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 1;
      },
      retryDelay: 400,
    },
    mutations: {
      /**
       * Las escrituras NO se reintentan solas. Un POST reintentado puede crear dos inmuebles,
       * y el usuario está delante para volver a pulsar si hace falta. La idempotencia se
       * resuelve donde toca (índices únicos y `Idempotency-Key`), no reintentando a ciegas.
       */
      retry: false,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('No se ha encontrado el elemento #root');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          {/* El visor de documentos: cualquier pantalla abre un PDF o una foto sin descargarlo. */}
          <DocumentViewerProvider>
            {/* La cola de subida de facturas: se suelta una tanda en Ingresos y gastos y se puede
                ir a la conciliación mientras se sube. */}
            <UploadQueueProvider>
              <App />
            </UploadQueueProvider>
          </DocumentViewerProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
